import { useCallback, useEffect, useMemo, useState, type DragEvent, type FormEvent } from 'react'
import { GripVertical, Pencil, Plus, Trash2 } from 'lucide-react'
import { adminApi } from '../api'
import RichTextEditor from '../editor/RichTextEditor'
import { Btn, Card, Field, ListSkeleton, Modal, Page, inputClass } from '../ui'
import { FAQ_LOCATIONS } from '../../data/faqLocations'

type Row = {
  id: number
  question: string
  answer_html: string
  location_key: string
  is_active: number
  sort_order: number
}

type LocMeta = {
  key: string
  label: string
  page: string
  blurb: string
  total: number
  active: number
}

type Edit = {
  id?: number
  question: string
  answer_html: string
  location_key: string
  is_active: boolean
  sort_order: number
}

export default function FaqsPage() {
  const [location, setLocation] = useState('global')
  const [locations, setLocations] = useState<LocMeta[]>(
    FAQ_LOCATIONS.map((l) => ({ ...l, total: 0, active: 0 }))
  )
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(true)
  const [savingOrder, setSavingOrder] = useState(false)
  const [edit, setEdit] = useState<Edit | null>(null)
  const [dragId, setDragId] = useState<number | null>(null)
  const [overId, setOverId] = useState<number | null>(null)

  const activeLoc = useMemo(
    () => locations.find((l) => l.key === location) || locations[0],
    [locations, location]
  )

  const loadLocations = useCallback(async () => {
    const { data } = await adminApi<{ locations?: LocMeta[] }>('/api/admin/website/faq-locations')
    if (data.locations?.length) setLocations(data.locations)
  }, [])

  const loadFaqs = useCallback(async (loc: string) => {
    setLoading(true)
    const { data } = await adminApi<{ faqs?: Row[] }>(
      `/api/admin/website/faqs?location=${encodeURIComponent(loc)}`
    )
    setRows(data.faqs || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    void loadLocations()
  }, [loadLocations])

  useEffect(() => {
    void loadFaqs(location)
  }, [location, loadFaqs])

  async function save(e: FormEvent) {
    e.preventDefault()
    if (!edit) return
    const body = {
      question: edit.question || '',
      answer_html: edit.answer_html || '',
      location_key: edit.location_key || location,
      is_active: edit.is_active ? 1 : 0,
      sort_order: edit.sort_order || 0,
    }
    await adminApi(edit.id ? `/api/admin/website/faqs/${edit.id}` : '/api/admin/website/faqs', {
      method: edit.id ? 'PUT' : 'POST',
      json: body,
    })
    setEdit(null)
    await Promise.all([loadFaqs(location), loadLocations()])
  }

  async function persistOrder(next: Row[]) {
    setRows(next)
    setSavingOrder(true)
    const { res } = await adminApi('/api/admin/website/faqs/reorder', {
      method: 'PUT',
      json: { ids: next.map((r) => r.id) },
    })
    setSavingOrder(false)
    if (!res.ok) void loadFaqs(location)
  }

  function reorder(fromId: number, toId: number) {
    if (fromId === toId) return
    const from = rows.findIndex((r) => r.id === fromId)
    const to = rows.findIndex((r) => r.id === toId)
    if (from < 0 || to < 0) return
    const next = [...rows]
    const [item] = next.splice(from, 1)
    next.splice(to, 0, item)
    void persistOrder(next.map((r, i) => ({ ...r, sort_order: i + 1 })))
  }

  return (
    <Page
      title="FAQs"
      actions={
        <Btn
          onClick={() =>
            setEdit({
              question: '',
              answer_html: '',
              location_key: location,
              is_active: true,
              sort_order: rows.length + 1,
            })
          }
        >
          <Plus className="h-4 w-4" /> Add FAQ
        </Btn>
      }
    >
      <Card className="space-y-3">
        <div>
          <p className="text-sm font-semibold text-navy-900">Location</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Select where these FAQs appear. City keys power local SEO / AEO on landing pages.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {locations.map((loc) => {
            const active = loc.key === location
            return (
              <button
                key={loc.key}
                type="button"
                onClick={() => setLocation(loc.key)}
                className={`rounded-xl border px-3 py-1.5 text-xs font-semibold transition ${
                  active
                    ? 'border-primary bg-primary text-white shadow-sm'
                    : 'border-primary/15 bg-white text-slate-700 hover:border-primary/35 hover:bg-primary/[0.04]'
                }`}
              >
                {loc.label}
                <span className={`ml-1.5 tabular-nums ${active ? 'text-white/80' : 'text-slate-400'}`}>
                  {loc.active}/{loc.total}
                </span>
              </button>
            )
          })}
        </div>
        {activeLoc && (
          <p className="text-xs text-slate-500">
            <span className="font-medium text-slate-700">{activeLoc.label}</span>
            {' · '}
            shows on <code className="rounded bg-slate-100 px-1">{activeLoc.page}</code>
            {' · '}
            {activeLoc.blurb}
            {savingOrder ? ' · saving order…' : ''}
          </p>
        )}
      </Card>

      {loading ? (
        <ListSkeleton rows={6} />
      ) : rows.length === 0 ? (
        <Card>
          <p className="text-sm text-slate-500 py-6 text-center">
            No FAQs for this location yet. Add location-specific questions for stronger SEO / AEO.
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <ul className="divide-y divide-primary/10">
            {rows.map((r) => {
              const dragging = dragId === r.id
              const over = overId === r.id && dragId !== r.id
              return (
                <li
                  key={r.id}
                  draggable
                  onDragStart={(e: DragEvent) => {
                    setDragId(r.id)
                    e.dataTransfer.effectAllowed = 'move'
                    e.dataTransfer.setData('text/plain', String(r.id))
                  }}
                  onDragOver={(e: DragEvent) => {
                    if (dragId == null) return
                    e.preventDefault()
                    e.dataTransfer.dropEffect = 'move'
                    if (overId !== r.id) setOverId(r.id)
                  }}
                  onDrop={(e: DragEvent) => {
                    e.preventDefault()
                    const from = Number(e.dataTransfer.getData('text/plain') || dragId)
                    if (from) reorder(from, r.id)
                    setDragId(null)
                    setOverId(null)
                  }}
                  onDragEnd={() => {
                    setDragId(null)
                    setOverId(null)
                  }}
                  className={`flex items-center gap-2 px-3 py-2.5 sm:px-4 ${
                    dragging ? 'opacity-40 bg-primary/[0.04]' : ''
                  } ${over ? 'bg-primary/[0.06] ring-1 ring-inset ring-primary/20' : 'hover:bg-primary/[0.03]'}`}
                >
                  <span className="cursor-grab active:cursor-grabbing text-slate-400 p-1">
                    <GripVertical className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-navy-900 truncate">{r.question}</p>
                    <p className="text-[0.7rem] text-slate-500 mt-0.5">
                      {r.is_active ? 'Active' : 'Inactive'} · order {r.sort_order}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Btn
                      variant="ghost"
                      onClick={() =>
                        setEdit({
                          id: r.id,
                          question: r.question,
                          answer_html: r.answer_html,
                          location_key: r.location_key || location,
                          is_active: !!r.is_active,
                          sort_order: r.sort_order,
                        })
                      }
                    >
                      <Pencil className="h-4 w-4" />
                    </Btn>
                    <Btn
                      variant="danger"
                      onClick={async () => {
                        if (!confirm('Delete this FAQ?')) return
                        await adminApi(`/api/admin/website/faqs/${r.id}`, { method: 'DELETE' })
                        await Promise.all([loadFaqs(location), loadLocations()])
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Btn>
                  </div>
                </li>
              )
            })}
          </ul>
        </Card>
      )}

      {edit && (
        <Modal size="lg" title={edit.id ? 'Edit FAQ' : 'Add FAQ'} onClose={() => setEdit(null)}>
          <form onSubmit={save} className="space-y-3">
            <Field label="Location">
              <select
                className={inputClass}
                value={edit.location_key}
                onChange={(e) => setEdit({ ...edit, location_key: e.target.value })}
              >
                {FAQ_LOCATIONS.map((loc) => (
                  <option key={loc.key} value={loc.key}>
                    {loc.label} ({loc.page})
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Question">
              <input
                className={inputClass}
                required
                value={edit.question || ''}
                onChange={(e) => setEdit({ ...edit, question: e.target.value })}
                placeholder="e.g. Does DigiSutra serve clients in Surat?"
              />
            </Field>
            <div className="space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Answer</span>
              <RichTextEditor
                value={edit.answer_html || ''}
                onChange={(html) => setEdit({ ...edit, answer_html: html })}
                minHeight={200}
                placeholder="Write a clear, direct answer for SEO / AEO…"
              />
            </div>
            <label className="text-sm flex gap-2 font-medium text-slate-700">
              <input
                type="checkbox"
                checked={!!edit.is_active}
                onChange={(e) => setEdit({ ...edit, is_active: e.target.checked })}
              />
              Active
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <Btn variant="ghost" onClick={() => setEdit(null)}>
                Cancel
              </Btn>
              <Btn type="submit">Save</Btn>
            </div>
          </form>
        </Modal>
      )}
    </Page>
  )
}
